import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const UserManagementTab = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showAddUserModal, setShowAddUserModal] = useState(false);

  const users = [
    {
      id: 'USR001',
      name: 'Dr. Anna Schmidt',
      email: 'anna.schmidt@arbeitsagentur.de',
      role: 'Administrator',
      department: 'IT-Verwaltung',
      status: 'Aktiv',
      lastLogin: '26.11.2025 18:45',
      permissions: ['Vollzugriff', 'Benutzerverwaltung', 'Systemkonfiguration']
    },
    {
      id: 'USR002',
      name: 'Michael Weber',
      email: 'michael.weber@arbeitsagentur.de',
      role: 'Supervisor',
      department: 'Kundenbetreuung',
      status: 'Aktiv',
      lastLogin: '26.11.2025 17:30',
      permissions: ['Berichterstattung', 'Massenvorgänge', 'Teamverwaltung']
    },
    {
      id: 'USR003',
      name: 'Sarah Müller',
      email: 'sarah.mueller@arbeitsagentur.de',
      role: 'Sachbearbeiter',
      department: 'Arbeitsvermittlung',
      status: 'Aktiv',
      lastLogin: '26.11.2025 19:00',
      permissions: ['Kundenverwaltung', 'Profilerstellung', 'Datenexport']
    },
    {
      id: 'USR004',
      name: 'Thomas Fischer',
      email: 'thomas.fischer@arbeitsagentur.de',
      role: 'Sachbearbeiter',
      department: 'Verwaltungsabwicklung',
      status: 'Inaktiv',
      lastLogin: '20.11.2025 16:15',
      permissions: ['Kundenverwaltung', 'Profilerstellung']
    },
    {
      id: 'USR005',
      name: 'Julia Hoffmann',
      email: 'julia.hoffmann@arbeitsagentur.de',
      role: 'Supervisor',
      department: 'Qualitätssicherung',
      status: 'Aktiv',
      lastLogin: '26.11.2025 18:20',
      permissions: ['Berichterstattung', 'Audit-Zugriff', 'Teamverwaltung']
    }
  ];

  const roleOptions = [
    { value: '', label: 'Alle Rollen' },
    { value: 'Administrator', label: 'Administrator' },
    { value: 'Supervisor', label: 'Supervisor' },
    { value: 'Sachbearbeiter', label: 'Sachbearbeiter' }
  ];

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedUsers(users?.map(u => u?.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers([...selectedUsers, userId]);
    } else {
      setSelectedUsers(selectedUsers?.filter(id => id !== userId));
    }
  };

  const filteredUsers = users?.filter(user => {
    const matchesSearch = user?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         user?.email?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
                         user?.id?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    const matchesRole = !selectedRole || user?.role === selectedRole;
    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Input
            type="search"
            placeholder="Benutzer suchen (Name, E-Mail, ID)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e?.target?.value)}
          />
          <Select
            placeholder="Nach Rolle filtern"
            options={roleOptions}
            value={selectedRole}
            onChange={setSelectedRole}
          />
        </div>
        <div className="flex gap-3">
          <Button
            variant="outline"
            iconName="Download"
            iconPosition="left"
          >
            Exportieren
          </Button>
          <Button
            variant="default"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => setShowAddUserModal(true)}
          >
            Benutzer hinzufügen
          </Button>
        </div>
      </div>
      {/* Bulk Actions */}
      {selectedUsers?.length > 0 && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
              {selectedUsers?.length} Benutzer ausgewählt
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" iconName="Edit">
                Rolle ändern
              </Button>
              <Button variant="outline" size="sm" iconName="Lock">
                Deaktivieren
              </Button>
              <Button variant="destructive" size="sm" iconName="Trash2">
                Löschen
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Users Table */}
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left w-12">
                  <Checkbox
                    checked={selectedUsers?.length === users?.length}
                    onChange={(e) => handleSelectAll(e?.target?.checked)}
                  />
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Benutzer
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Rolle
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Abteilung
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Letzter Login
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wider">
                  Aktionen
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
              {filteredUsers?.map((user) => (
                <tr key={user?.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors">
                  <td className="px-4 py-4">
                    <Checkbox
                      checked={selectedUsers?.includes(user?.id)}
                      onChange={(e) => handleSelectUser(user?.id, e?.target?.checked)}
                    />
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white font-semibold">
                        {user?.name?.split(' ')?.map(n => n?.[0])?.join('')}
                      </div>
                      <div>
                        <div className="font-medium text-slate-900 dark:text-slate-100">{user?.name}</div>
                        <div className="text-sm text-slate-500 dark:text-slate-400">{user?.email}</div>
                        <div className="text-xs text-slate-400 dark:text-slate-500">ID: {user?.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      user?.role === 'Administrator' ?'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
                        : user?.role === 'Supervisor' ?'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' :'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                      {user?.role}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                    {user?.department}
                  </td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
                      user?.status === 'Aktiv' ?'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' :'bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300'
                    }`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${
                        user?.status === 'Aktiv' ? 'bg-emerald-600' : 'bg-slate-600'
                      }`} />
                      {user?.status}
                    </span>
                  </td>
                  <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                    {user?.lastLogin}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors">
                        <Icon name="Edit" size={16} className="text-slate-600 dark:text-slate-400" />
                      </button>
                      <button className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-600 rounded transition-colors">
                        <Icon name="Key" size={16} className="text-slate-600 dark:text-slate-400" />
                      </button>
                      <button className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded transition-colors">
                        <Icon name="Trash2" size={16} className="text-red-600 dark:text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Gesamt Benutzer</div>
              <div className="text-2xl font-bold text-slate-900 dark:text-slate-100 mt-1">{users?.length}</div>
            </div>
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Icon name="Users" size={24} className="text-blue-600 dark:text-blue-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Aktive Benutzer</div>
              <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
                {users?.filter(u => u?.status === 'Aktiv')?.length}
              </div>
            </div>
            <div className="w-12 h-12 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <Icon name="UserCheck" size={24} className="text-emerald-600 dark:text-emerald-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Administratoren</div>
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400 mt-1">
                {users?.filter(u => u?.role === 'Administrator')?.length}
              </div>
            </div>
            <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Icon name="Shield" size={24} className="text-purple-600 dark:text-purple-400" />
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-600 dark:text-slate-400">Inaktive Benutzer</div>
              <div className="text-2xl font-bold text-slate-600 dark:text-slate-400 mt-1">
                {users?.filter(u => u?.status === 'Inaktiv')?.length}
              </div>
            </div>
            <div className="w-12 h-12 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center">
              <Icon name="UserX" size={24} className="text-slate-600 dark:text-slate-400" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementTab;